/**
 * Shared model configuration for both client and server
 * This file can be imported by both React components and Cloudflare Workers
 */

import type { SubAgentId } from "@/shared/sub-agents";

export type OpenAIModelId = "gpt-5.4" | "gpt-5.4-mini" | "gpt-5-nano";
export type ReasoningEffort = "low" | "medium" | "high";

export interface ModelConfig {
  /** Unique identifier for the model and OpenAI model ID */
  id: OpenAIModelId;
  /** Display name shown in the UI */
  name: string;
  /** Reasoning effort level (null if not supported) */
  reasoningEffort: ReasoningEffort | null;
}

export const DEFAULT_MODEL_ID: OpenAIModelId = "gpt-5-nano";

const LEGACY_MODEL_ID_ALIASES = {
  "openai/gpt-5.4": "gpt-5.4",
  "openai/gpt-5.4-mini": "gpt-5.4-mini",
  "openai/gpt-5.4-nano": "gpt-5-nano",
  "openai/gpt-5-mini": "gpt-5.4-mini",
  "openai/gpt-5-nano": "gpt-5-nano",
  "openrouter/openai/gpt-5.4": "gpt-5.4",
  "openrouter/openai/gpt-5.4-mini": "gpt-5.4-mini",
  "openrouter/openai/gpt-5.4-nano": "gpt-5-nano",
  "openrouter/openai/gpt-5-mini": "gpt-5.4-mini",
  "openrouter/openai/gpt-5-nano": "gpt-5-nano",
} as const;

/**
 * Available models configuration
 */
export const AVAILABLE_MODELS: ModelConfig[] = [
  {
    id: "gpt-5.4",
    name: "GPT-5.4",
    reasoningEffort: "low"
  },
  {
    id: "gpt-5.4-mini",
    name: "GPT-5.4 Mini",
    reasoningEffort: "low"
  },
  {
    id: "gpt-5-nano",
    name: "GPT-5 Nano",
    reasoningEffort: "low"
  }
];

const AVAILABLE_MODELS_BY_ID = new Map(
  AVAILABLE_MODELS.map((model) => [model.id, model])
);

export function resolveCanonicalModelId(
  modelId: string | null | undefined
): OpenAIModelId | undefined {
  if (typeof modelId !== "string") {
    return undefined;
  }

  if (AVAILABLE_MODELS_BY_ID.has(modelId as OpenAIModelId)) {
    return modelId as OpenAIModelId;
  }

  const aliasedModelId =
    LEGACY_MODEL_ID_ALIASES[
      modelId as keyof typeof LEGACY_MODEL_ID_ALIASES
    ];

  return aliasedModelId as OpenAIModelId | undefined;
}

/**
 * Get model configuration by ID
 */
export function getModelConfig(modelId: string): ModelConfig | undefined {
  const canonicalModelId = resolveCanonicalModelId(modelId);
  return canonicalModelId
    ? AVAILABLE_MODELS_BY_ID.get(canonicalModelId)
    : undefined;
}

export function normalizeSelectedModelId(
  modelId: string | null | undefined
): OpenAIModelId {
  const canonicalModelId = resolveCanonicalModelId(modelId);
  return canonicalModelId ?? DEFAULT_MODEL_ID;
}

/**
 * Get default model
 */
export function getDefaultModel(): ModelConfig {
  const preferred = AVAILABLE_MODELS_BY_ID.get(DEFAULT_MODEL_ID) ?? AVAILABLE_MODELS[0];

  if (!preferred) {
    throw new Error("No models configured in AVAILABLE_MODELS.");
  }

  return preferred;
}

/**
 * Model selection state interface
 */
export interface ModelSelectionState {
  selectedModelId: string;
  reasoningEffort?: string;
  clearedSubAgentContextAt?: Partial<Record<SubAgentId, string>>;
}
